import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';

import { INTERNAL_CUSTOMER_ID } from '@/pages/developer/types';
import type { Customer, InvoiceDraft, JournalEntry } from '@/pages/developer/types';

const CREATE_CUSTOMER_VALUE = '__create_customer__';

export type InvoiceEditorDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceDraft: InvoiceDraft | null;
  setInvoiceDraft: Dispatch<SetStateAction<InvoiceDraft | null>>;
  onCancel: () => void;
  handleSaveInvoiceEdit: () => void;
  onDeleteInvoice: (invoiceId: string) => void;
  customers: Customer[];
  createCustomer: (name: string) => Promise<string | null>;
};

export function InvoiceEditorDialog({
  open,
  onOpenChange,
  invoiceDraft,
  setInvoiceDraft,
  onCancel,
  handleSaveInvoiceEdit,
  onDeleteInvoice,
  customers,
  createCustomer,
}: InvoiceEditorDialogProps) {
  const [newCustomerName, setNewCustomerName] = useState('');

  useEffect(() => {
    if (!open) {
      setNewCustomerName('');
    }
  }, [open]);

  const visibleCustomers = useMemo(() => customers.filter((customer) => customer.id !== INTERNAL_CUSTOMER_ID), [customers]);
  const resolveCustomerName = (customerId: string) => {
    if (customerId === INTERNAL_CUSTOMER_ID) return 'Internal';
    return visibleCustomers.find((customer) => customer.id === customerId)?.name ?? '';
  };

  const handleCreateCustomer = async () => {
    const name = newCustomerName.trim();
    if (!name) return;
    const createdId = await createCustomer(name);
    if (!createdId) return;
    setNewCustomerName('');
    setInvoiceDraft((prev) =>
      prev
        ? {
            ...prev,
            customerId: createdId,
            customer: resolveCustomerName(createdId) || name,
          }
        : prev
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit invoice</DialogTitle>
          <DialogDescription>Update invoice details and save changes.</DialogDescription>
        </DialogHeader>
        {invoiceDraft && (
          <div className="space-y-3">
            <Input
              type="date"
              value={invoiceDraft.date}
              onChange={(event) => setInvoiceDraft((prev) => (prev ? { ...prev, date: event.target.value } : prev))}
            />
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={invoiceDraft.entryType}
              disabled={invoiceDraft.id === 'retainer-paid'}
              onChange={(event) =>
                setInvoiceDraft((prev) => (prev ? { ...prev, entryType: event.target.value as JournalEntry['entryType'] } : prev))
              }
            >
              <option value="retainer">Retainer</option>
              <option value="service-charge">Service charge</option>
              <option value="initialization-webapp-build">Initialization WebApp Build</option>
            </select>
            <div className="space-y-2">
              <select
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={(invoiceDraft.customerId ?? '').trim()}
                onChange={(event) => {
                  const selected = event.target.value;
                  setInvoiceDraft((prev) => {
                    if (!prev) return prev;
                    if (!selected) {
                      return { ...prev, customerId: null, customer: '' };
                    }
                    if (selected === CREATE_CUSTOMER_VALUE) {
                      return { ...prev, customerId: CREATE_CUSTOMER_VALUE, customer: '' };
                    }

                    const name = resolveCustomerName(selected);
                    return { ...prev, customerId: selected, customer: name || prev.customer };
                  });
                }}
              >
                <option value="">Select customer</option>
                <option value={INTERNAL_CUSTOMER_ID}>Internal</option>
                {visibleCustomers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
                <option value={CREATE_CUSTOMER_VALUE}>Create customer…</option>
              </select>
              {(invoiceDraft.customerId ?? '').trim() === INTERNAL_CUSTOMER_ID ? (
                <p className="text-xs text-muted-foreground">Internal = liability held by HappyApps for eventual recovery.</p>
              ) : null}
              {(invoiceDraft.customerId ?? '').trim() === CREATE_CUSTOMER_VALUE ? (
                <div className="flex gap-2">
                  <Input
                    placeholder="New customer name"
                    value={newCustomerName}
                    onChange={(event) => setNewCustomerName(event.target.value)}
                  />
                  <Button type="button" variant="outline" onClick={handleCreateCustomer} disabled={!newCustomerName.trim()}>
                    Create
                  </Button>
                </div>
              ) : null}
            </div>
            <Input
              type="number"
              min="0"
              step="0.01"
              placeholder="Amount"
              value={invoiceDraft.amount}
              onChange={(event) => setInvoiceDraft((prev) => (prev ? { ...prev, amount: event.target.value } : prev))}
            />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="Misc"
                value={invoiceDraft.misc}
                onChange={(event) => setInvoiceDraft((prev) => (prev ? { ...prev, misc: event.target.value } : prev))}
              />
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="Taxes"
                value={invoiceDraft.taxes}
                onChange={(event) => setInvoiceDraft((prev) => (prev ? { ...prev, taxes: event.target.value } : prev))}
              />
            </div>
            <Input
              placeholder="Notes (optional)"
              value={invoiceDraft.notes}
              onChange={(event) => setInvoiceDraft((prev) => (prev ? { ...prev, notes: event.target.value } : prev))}
            />
          </div>
        )}
        <DialogFooter>
          {invoiceDraft && invoiceDraft.id !== 'retainer-paid' && (
            <Button type="button" variant="destructive" className="gap-2" onClick={() => onDeleteInvoice(invoiceDraft.id)}>
              <Trash2 className="w-4 h-4" />
              Delete
            </Button>
          )}
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={handleSaveInvoiceEdit}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
